/* =========================================================================
   Paul Smith header / footer — standalone renderer
   =========================================================================

   Mirrors the Nuxt front end so both render the same markup and behaviour:

     components/layout/PsHeader.vue          header rows + right-side actions
     components/layout/PsHeaderStripe.vue    4px stripe
     components/layout/nav/PsMegaMenu.vue    slide-in mega menu + off-canvas
     components/layout/PsFooter.vue          footer
     stores/GlobalStore.js                   data shaping
     server/utils/shopify.ts                 Shopify menu queries

   Data sources — deliberately the same ones GlobalStore uses:

     Shopify  main-menu                      header nav (3 levels)
     Shopify  footer / footer_new / footer3
              / sns / link-list / policy2    footer columns + policy row
     Kuroco   /layout_decoration/9858        stripe (ext_5), footer image
                                             (ext_2), quote (ext_1)
     Kuroco   /info                          help panel FAQ links
     Shopify  /cart.js                       bag count (Ajax Cart API)

   Kuroco /footer_category and /footer_content are NOT used: GlobalStore
   dropped them, so reading them here produced a completely different footer.
   ========================================================================= */
(function () {
    'use strict';

    // Assets are resolved against this file's own URL, not the host page's, so
    // the folder can be dropped anywhere (including Shopify's /cart route).
    const SCRIPT_SRC = (document.currentScript && document.currentScript.src) || '';
    const ASSET_BASE = SCRIPT_SRC ? SCRIPT_SRC.replace(/\/[^/]*$/, '/') : './';
    const SITE_PAGE_BASE_URL = 'https://www.paulsmith.co.jp';

    const CONFIG = {
        kurocoBaseUrl: '',
        kurocoApiKey: '',
        shopifyStorefrontDomain: '',
        shopifyStorefrontAccessToken: '',
        shopifyApiVersion: '2025-10',
        countryLabel: 'Japan',
        currencyLabel: 'JPY',
        // baseStore.currencyLabel, as shown in the off-canvas menu.
        currencyDisplayLabel: 'JPY ¥',
        // Numeric Shopify customer id, used to pick the same localStorage key
        // as Nuxt's getFavoritesStorageKey(). Optional: on Liquid pages it is
        // read from window.__st.cid when logged in.
        customerId: '',
        zetaApiEndpoint: '',
        zetaSearchProxyPath: '/api/zeta/search',
        shopifyPageBaseUrl: '',
        // Origin serving the Nuxt /api/favorites/* routes. Leave empty to call
        // them same-origin (single-domain setup). Setting it makes the wishlist
        // fetch cross-origin with credentials, which additionally requires the
        // Nuxt server to allow this page's origin via
        // NUXT_FAVORITES_ALLOWED_ORIGINS (server/middleware/favorites-cors.ts) —
        // without that, the tray falls back to the favorites stored on this
        // domain.
        favoritesApiBaseUrl: '',
        assetBaseUrl: ASSET_BASE,
        globalsUrl: '',
        ...(window.PS_HEADER_FOOTER_CONFIG || {}),
    };

    // locales/ja-JP.json
    const T = {
        Search: '検索',
        Stores: '店舗検索',
        Help: 'ヘルプ',
        Login: 'ログイン',
        Wishlist: 'お気に入り',
        Bag: 'カート',
        Account: 'アカウント',
        Close: '閉じる',
        'Type to search ...': 'キーワードで探す',
        'Enter Your Email': 'Enter Your Email',
        // Wishlist tray (trays/Wishlist.vue + WishlistItem.vue). The English
        // values are English in ja-JP.json too — not an omission here.
        'Your wishlist is empty': 'お気に入りの商品はありません',
        'Add to Bag': 'カートに入れる',
        'Add all to bag': 'Add all to bag',
        'Remove all': 'Remove all',
        'View bag': 'View bag',
        '{product} was added to your bag': '{product} was added to your bag',
    };

    const LOGO =
        '<svg fill="none" height="100%" viewBox="0 0 143 26" width="100%" xmlns="http://www.w3.org/2000/svg"><path d="M7.09466 0.0821648C5.55322 0.471212 4.07273 1.65565 2.85352 3.46257C1.42529 5.58072 0.354116 8.81415 0.0754375 11.8487C-0.21195 14.9784 0.354116 20.6585 1.22499 23.3732C1.94781 25.6124 2.59225 26.1917 3.85502 25.7162C4.22078 25.5778 4.28174 25.3963 4.12499 24.817C3.9421 24.1081 3.62859 22.0505 3.26283 19.1801C3.09736 17.8314 2.95802 16.7162 2.95802 16.6989C2.95802 16.6816 3.3412 16.4222 3.80277 16.1196C5.30937 15.1427 7.47784 13.4654 8.27033 12.6701C9.75952 11.1657 10.6478 9.57496 11.2923 7.23202C11.5187 6.40205 11.51 3.86891 11.2835 3.08216C10.7349 1.19744 9.60277 0.0908089 8.15712 0.00435448C7.80877 -0.0129375 7.3385 0.0216446 7.09466 0.0821648ZM8.48805 1.58649C10.2211 2.19167 10.3691 5.10522 8.84511 8.31271C7.62589 10.8545 5.88415 12.8776 3.5415 14.477L2.7403 15.0216L2.67934 14.7882C2.64451 14.6585 2.56613 13.8458 2.51388 12.9727C2.38325 11.1312 2.47904 9.67871 2.8361 7.92366C3.35862 5.33 4.24691 3.47986 5.52709 2.32136C6.52859 1.40493 7.37334 1.19744 8.48805 1.58649Z" fill="currentColor"/><path d="M68.4915 3.34185C67.1591 3.62715 66.0879 4.41389 65.5567 5.50323C65.2867 6.0479 65.2954 7.07672 65.5654 7.77701C65.9399 8.73666 67.4813 10.7511 69.8936 13.4658C72.8633 16.8116 74.4744 19.6041 74.2306 21.0133C74.1435 21.5493 73.9345 21.9989 73.6906 22.1891C73.3597 22.4571 72.393 22.7251 71.8008 22.7165C70.4597 22.6992 69.4147 22.1718 67.0981 20.3476C66.6192 19.9672 66.0618 19.5868 65.8528 19.4917C65.4957 19.3274 65.4609 19.3274 65.3041 19.4744C65.2083 19.5609 65.1387 19.7338 65.1387 19.9067C65.1387 20.6502 67.1504 22.5263 69.0576 23.5551C70.9387 24.5753 72.393 24.8779 73.8735 24.5666C74.9621 24.3418 75.5456 23.7539 75.981 22.4398C76.1987 21.8087 76.2162 21.6444 76.1639 21.0306C76.0855 20.0277 75.833 19.1805 75.2321 17.9787C74.2741 16.0681 73.2465 14.6243 70.5381 11.3822C68.1606 8.54646 67.969 8.30438 67.6642 7.74242C67.2723 7.00756 67.2375 6.56663 67.5249 5.97873C67.8122 5.39948 68.1954 5.09689 68.7615 4.99314C69.824 4.7943 71.8966 5.51188 73.2813 6.55799C74.2915 7.32744 74.518 8.0796 73.8474 8.47729C73.5165 8.6675 73.4729 9.08248 73.7864 9.16894C74.2132 9.28133 74.7008 9.21216 75.1624 8.97874C75.5369 8.79718 75.6936 8.64156 75.9201 8.2698C76.0942 7.97585 76.1987 7.69055 76.1987 7.4917C76.1987 6.83464 75.2669 5.64156 74.1435 4.84617C73.4642 4.36202 72.1666 3.74819 71.3219 3.51476C70.5903 3.31591 69.0576 3.21217 68.4915 3.34185Z" fill="currentColor"/><path d="M43.1316 4.62099C42.1823 4.95817 41.5553 5.56335 41.0066 6.66998C40.4406 7.81119 40.2925 8.52877 40.2838 10.085C40.2751 11.2781 40.3012 11.5288 40.519 12.376C40.9631 14.1051 42.1649 16.7507 43.5061 18.9639C43.8631 19.5432 44.1505 20.0619 44.1505 20.1138C44.1505 20.304 43.5409 21.5144 43.2796 21.8429C42.879 22.3357 42.2085 22.6383 41.4769 22.6383C39.4913 22.6469 38.2721 21.4279 35.764 16.9495C35.0325 15.6527 34.6319 15.3328 34.1529 15.6786C33.9961 15.791 33.5085 16.6296 32.7421 18.1167C32.0976 19.3703 31.4271 20.572 31.2616 20.7881C30.8523 21.3328 30.2862 21.6354 29.8247 21.5489C29.0496 21.402 28.6838 20.7017 27.5691 17.2521C26.8463 15.0043 26.5676 14.5634 26.0538 14.84C25.7055 15.0302 25.3484 15.8342 24.6517 18.0389C23.4238 21.938 23.119 22.4481 22.0478 22.3443C21.534 22.2925 21.0463 21.9985 20.7415 21.5576C20.4976 21.1858 20.0535 19.889 19.4961 17.8659C19.0085 16.1196 18.7211 15.3242 18.1724 14.2781C17.5454 13.0936 16.7877 12.5489 15.5598 12.4193C13.6961 12.2118 12.1199 13.4221 10.7526 16.1109C10.2475 17.1138 9.9514 18.255 9.83819 19.7074C9.60305 22.5172 10.2388 24.0821 11.9283 24.8602C12.9995 25.353 14.7151 25.3789 15.7514 24.9121C16.3175 24.6527 16.5613 24.4366 17.2145 23.6325C18.3988 22.1714 19.3742 22.1801 20.3409 23.6585C21.0028 24.6786 21.6124 25.0331 22.4571 24.9121C23.3889 24.7737 24.0421 24.0994 25.3832 21.8861C25.9319 20.961 26.1757 20.719 26.5241 20.719C26.8724 20.719 27.3253 21.1945 27.8565 22.085C28.5358 23.2435 29.0844 23.6152 30.0859 23.6066C30.5388 23.6066 30.7913 23.5547 31.0874 23.3991C31.61 23.1311 32.4199 22.3443 33.3517 21.2118C34.2313 20.1484 34.4577 19.9495 34.8496 19.889C35.2937 19.8198 35.6508 20.1224 36.5913 21.3674C37.7061 22.8371 38.5421 23.6757 39.2649 24.0302C39.7526 24.2636 39.9355 24.3069 40.667 24.3328C41.1286 24.3501 41.7295 24.3242 41.9994 24.2636C43.1054 24.0389 44.1679 23.2781 44.8559 22.2147C45.0562 21.8861 45.2391 21.6095 45.2565 21.5922C45.2652 21.5749 45.5003 21.7651 45.7703 22.0072C47.1289 23.2348 48.2436 23.7276 49.8634 23.8054C51.3787 23.8832 52.4063 23.6412 53.4514 22.9668C54.0784 22.5691 54.2961 22.2838 54.2178 21.9639C54.1481 21.6873 53.7649 21.3242 53.5385 21.3242C53.4514 21.3242 53.0595 21.4971 52.6589 21.7046C51.7358 22.1974 51.161 22.3616 50.3163 22.3616C48.9316 22.3616 47.8343 21.7391 46.6238 20.2694L46.1187 19.6642L46.319 18.9639C46.6151 17.8659 47.1724 15.1426 47.3727 13.8285C47.9301 10.1196 47.4859 7.0158 46.1883 5.44232C45.5352 4.65557 44.1157 4.26653 43.1316 4.62099ZM44.7688 6.02157C45.2042 6.47114 45.4133 7.16278 45.5526 8.53742C45.7355 10.4135 45.4916 14.0014 45.0301 16.2665L44.7775 17.4769L43.7412 15.3587C42.4958 12.8429 42.3042 12.376 42.0778 11.2953C41.8253 10.1109 41.7643 9.21177 41.8862 8.39909C42.1475 6.66998 42.94 5.68439 44.0547 5.67575C44.3943 5.67575 44.4814 5.71898 44.7688 6.02157ZM16.0649 14.2781C16.7529 14.6757 17.3625 15.8429 17.5976 17.2175C17.6499 17.5461 17.7022 18.2896 17.7022 18.8602C17.7109 20.4164 17.4496 21.1167 16.4742 22.0936C15.8124 22.7593 15.316 23.0273 14.5148 23.157C13.461 23.3299 12.5466 22.8717 12.294 22.0417C12.0763 21.3069 11.9805 19.9668 12.0937 19.085C12.3289 17.3126 12.9211 15.5489 13.5046 14.8919C14.2274 14.0792 15.2898 13.8198 16.0649 14.2781Z" fill="currentColor"/><path d="M99.5276 4.88932C99.1879 5.15733 99.0747 5.5118 99.1183 6.1775C99.1705 6.83456 99.3186 7.18903 99.754 7.61266C100.12 7.97577 100.555 8.1141 100.973 8.00171C102.027 7.70776 101.888 5.80574 100.747 4.95848C100.39 4.70776 99.8063 4.66453 99.5276 4.88932Z" fill="currentColor"/><path d="M111.12 7.13697C110.868 7.20613 110.694 7.60382 110.694 8.13985C110.685 8.33005 110.763 9.12544 110.859 9.91218C111.582 15.99 111.547 17.4597 110.659 19.5001C110.284 20.356 109.823 21.039 109.196 21.6701C108.647 22.2148 107.768 22.7076 107.358 22.7076C107.045 22.7076 106.522 22.4482 106.113 22.0851C105.651 21.6874 104.484 19.8891 103.37 17.8661C102.116 15.5837 101.828 15.1946 101.393 15.186C101.097 15.186 100.835 15.6182 100.191 17.1744C99.0067 20.0534 98.3013 21.2292 97.6829 21.3416C96.873 21.4972 96.455 21.039 94.8265 18.2551C93.3983 15.7998 92.8235 15.0822 92.179 14.9612C91.491 14.8401 91.2385 15.1341 90.176 17.2609C89.0004 19.6125 88.5214 20.2782 87.9031 20.4511C87.0235 20.6932 86.5619 20.1571 84.9073 17.0102C84.1148 15.5058 83.7316 15.013 83.3658 15.013C83 15.013 82.6082 15.5145 82.2424 16.4569C81.9985 17.1053 81.8244 17.3992 81.537 17.6845C81.1973 18.0303 81.1364 18.0563 80.849 18.013C80.4223 17.9439 80.1436 17.6672 79.8562 17.0447C79.5949 16.4828 79.2988 16.1629 79.0289 16.137C78.576 16.0851 78.4802 16.137 78.3757 16.4482C78.2538 16.8027 78.2973 17.1917 78.6021 18.428C78.7154 18.9035 78.9157 19.8718 79.0376 20.5894C79.4295 22.9237 79.7778 24.0822 80.1349 24.2724C80.2568 24.3416 80.37 24.3243 80.579 24.2032C80.8055 24.0736 81.0319 23.7105 81.8331 22.1456C82.9304 20.0015 83.2265 19.5952 83.7229 19.5952C83.897 19.5952 84.1322 19.6557 84.2541 19.7335C84.5154 19.8978 85.0901 20.745 86.1352 22.4741C87.3109 24.4453 87.7724 24.8084 88.4778 24.3329C88.8784 24.0563 89.2877 23.4424 90.3589 21.4453C91.6826 18.9986 91.9178 18.696 92.4925 18.7133C93.076 18.7306 93.5028 19.2321 95.2968 22.0505C96.4898 23.9179 97.0298 24.6096 97.4739 24.8344C97.7613 24.99 98.1706 24.9468 98.4841 24.7306C98.7628 24.5318 99.3811 23.5635 100.148 22.1024C100.87 20.7364 101.436 20.0275 101.82 20.0275C102.029 20.0275 102.612 20.3646 102.804 20.5894C102.847 20.6499 103.378 21.2983 103.971 22.0246C105.26 23.6154 105.974 24.3675 106.505 24.6788C107.297 25.1456 108.508 24.8862 109.631 24.0044C109.866 23.8142 110.581 23.1744 111.208 22.5779C111.835 21.9727 112.392 21.4972 112.444 21.5145C112.496 21.5318 112.801 21.9813 113.123 22.5174C114.761 25.2061 115.51 25.8545 117.016 25.8459C117.931 25.8459 118.593 25.552 119.028 24.9554C119.847 23.8315 120.195 23.5548 120.831 23.5548C121.301 23.5548 121.458 23.6413 122.102 24.2292C122.572 24.6615 122.642 24.696 123.043 24.696C123.565 24.696 123.731 24.5491 124.462 23.4165C125.02 22.552 126.317 21.1514 127.179 20.4684C127.946 19.8632 128.407 19.6557 128.973 19.6471C129.714 19.6384 130.097 19.9151 131.978 21.8344C135.714 25.6557 135.653 25.6038 136.742 25.8632C137.917 26.1398 139.267 25.9583 141.296 25.2753C141.993 25.0419 142.324 24.6874 142.28 24.2292C142.237 23.7883 141.958 23.7623 140.66 24.0736C138.509 24.6009 137.639 24.6009 136.811 24.0822C136.332 23.7883 132.1 19.5606 131.072 18.3589C130.018 17.1312 129.478 16.8891 128.538 17.2177C127.911 17.4338 126.152 18.7998 124.602 20.2609C123.931 20.892 123.435 21.1427 122.86 21.1514C122.381 21.1514 121.972 20.8661 121.771 20.3906C121.588 19.9583 121.493 18.2119 121.484 15.4453C121.484 11.1139 121.179 9.43668 120.326 9.02169C119.376 8.55483 118.915 9.61823 119.133 11.771C119.263 13.1456 119.272 17.6499 119.141 18.8603C118.967 20.4165 118.549 21.8517 118.079 22.4309C117.608 23.0275 116.668 23.1398 116.058 22.6816C114.926 21.8257 113.672 17.9093 113.498 14.7105C113.402 12.843 113.751 12.4367 115.884 11.9439C117.391 11.5981 117.652 11.4338 117.652 10.8632C117.652 10.2753 117.19 9.82573 116.59 9.82573C116.433 9.82573 115.971 9.90354 115.562 9.99864C115.153 10.0937 114.595 10.1715 114.325 10.1715C113.707 10.1715 113.463 9.98135 113.254 9.307C112.758 7.7508 111.939 6.92083 111.12 7.13697Z" fill="currentColor"/></svg>';

    const FALLBACK_STRIPE = 'ps-img-stripe-header-desktop.png';

    const emptyData = {
        stripe: { desktop: null, mobile: null },
        header: { menu: [], cartCount: 0 },
        footer: {
            columns: [],
            mainMenu: [],
            extraMenu: [],
            textBeforeExtraMenu: 'Paul Smith. All right reserved',
            image: { landscape: null, portrait: null },
            quote: '',
            showSignup: false,
            showCountrySwitcher: false,
            countryLabel: CONFIG.countryLabel,
            currencyLabel: CONFIG.currencyLabel,
            emailPlaceholder: T['Enter Your Email'],
        },
        helpFaqItems: [],
    };

    const state = {
        data: emptyData,
        // 'offcanvas' | menu id | null
        activeMenuId: null,
        activeTabId: null,
        activePanel: null,
        data_loaded: false,
        wishlist: {
            // Hydrated products (same shape as customerStore.wishlist.items).
            items: [],
            hydrated: false,
            // key -> 'added' | 'removing' (WishlistItem's itemStatus / isRemoving)
            status: {},
        },
    };

    const headerRoot = document.getElementById('ps-header-root') || document.getElementById('ps-header-footer');
    const footerRoot = document.getElementById('ps-footer-root') || headerRoot;

    /* --------------------------------------------------------------------- *
     * Helpers
     * --------------------------------------------------------------------- */

    function escapeHtml(value) {
        return String(value === undefined || value === null ? '' : value)
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;')
            .replace(/'/g, '&#39;');
    }

    function asset(path) {
        return new URL(path, CONFIG.assetBaseUrl || './').href;
    }

    /**
     * SvgIcon.vue renders a masked span so the glyph inherits currentColor.
     * Doing the same here keeps colour-inversion and hover states identical;
     * an <img> could not follow currentColor.
     */
    function icon(name, extraClass) {
        return (
            '<span class="ps-icon' +
            (extraClass ? ' ' + escapeHtml(extraClass) : '') +
            '" style="--ps-icon:url(' +
            escapeHtml(asset('ps-img-' + name + '.svg')) +
            ')" aria-hidden="true"></span>'
        );
    }

    function normalizeBaseUrl(value) {
        return String(value || '').replace(/\/$/, '');
    }

    function shopifyPageUrl(path) {
        return normalizeBaseUrl(CONFIG.shopifyPageBaseUrl) + path;
    }

    function sitePageUrl(rawUrl) {
        const url = String(rawUrl || '').trim();
        if (!url || url === '#') {
            return url;
        }
        if (url.charAt(0) === '/' && url.charAt(1) !== '/') {
            return SITE_PAGE_BASE_URL + url;
        }

        try {
            const parsedUrl = new URL(url);
            const shopifyPageBaseUrl = normalizeBaseUrl(CONFIG.shopifyPageBaseUrl);
            const shopifyPageOrigin = shopifyPageBaseUrl ? new URL(shopifyPageBaseUrl).origin : '';
            const storefrontDomain = String(CONFIG.shopifyStorefrontDomain || '').toLowerCase();
            if (
                (shopifyPageOrigin && parsedUrl.origin === shopifyPageOrigin) ||
                (storefrontDomain && parsedUrl.hostname.toLowerCase() === storefrontDomain)
            ) {
                return SITE_PAGE_BASE_URL + parsedUrl.pathname + parsedUrl.search + parsedUrl.hash;
            }
        } catch (error) {
            return url;
        }

        return url;
    }

    function isExternalSiteUrl(rawUrl) {
        if (!/^https?:\/\//i.test(rawUrl)) {
            return false;
        }
        try {
            return new URL(rawUrl).origin !== SITE_PAGE_BASE_URL;
        } catch (error) {
            return true;
        }
    }

    async function fetchJson(url, options) {
        const response = await fetch(url, options);
        if (!response.ok) {
            throw new Error(url + ' returned ' + response.status);
        }
        return response.json();
    }

    function readCookie(name) {
        const match = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'));
        return match ? decodeURIComponent(match[1]) : '';
    }

    /** GlobalStore.js — mirrors useShopifyAccountUrl(). */
    function isLoggedIn() {
        return readCookie('ps_logged_in') === '1';
    }

    function accountUrl() {
        if (isLoggedIn()) {
            return shopifyPageUrl('/account/');
        }
        return (
            shopifyPageUrl('/account/login') + '?return_url=' + encodeURIComponent(location.pathname + location.search)
        );
    }

    /* --------------------------------------------------------------------- *
     * Shopify / Kuroco transport
     * --------------------------------------------------------------------- */

    async function shopifyRequest(query, variables) {
        if (!CONFIG.shopifyStorefrontDomain || !CONFIG.shopifyStorefrontAccessToken) {
            return null;
        }
        const url = 'https://' + CONFIG.shopifyStorefrontDomain + '/api/' + CONFIG.shopifyApiVersion + '/graphql.json';
        const result = await fetchJson(url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'X-Shopify-Storefront-Access-Token': CONFIG.shopifyStorefrontAccessToken,
            },
            body: JSON.stringify({ query: query, variables: variables }),
        });
        if (result.errors) {
            throw new Error('Shopify returned errors: ' + JSON.stringify(result.errors));
        }
        return result.data;
    }

    async function kurocoFetch(endpoint) {
        const baseUrl = normalizeBaseUrl(CONFIG.kurocoBaseUrl);
        if (!baseUrl || !CONFIG.kurocoApiKey) {
            return null;
        }
        return fetchJson(baseUrl + endpoint, {
            headers: { 'X-RCMS-API-ACCESS-TOKEN': CONFIG.kurocoApiKey },
        });
    }

    /* --------------------------------------------------------------------- *
     * Header menu (server/utils/shopify.ts + GlobalStore.js)
     * --------------------------------------------------------------------- */

    const MENU_ITEM_FIELDS =
        'id title url type resource { ... on Collection { id handle title } ... on Product { id handle title } ... on Page { id handle title } }';

    async function fetchShopifyMenu(handle) {
        const query =
            'query getMenu($handle: String!) { menu(handle: $handle) { handle title items { ' +
            MENU_ITEM_FIELDS +
            ' items { ' +
            MENU_ITEM_FIELDS +
            ' items { ' +
            MENU_ITEM_FIELDS +
            ' } } } } }';
        const response = await shopifyRequest(query, { handle: handle });
        if (!response || !response.menu) {
            return null;
        }
        return { ...response.menu, items: transformShopifyItems(response.menu.items) };
    }

    /** Collection links become /parent-handle/handle, matching the Nuxt routes. */
    function transformMenuUrl(rawUrl, parentHandle) {
        if (!rawUrl) {
            return rawUrl;
        }
        const match = String(rawUrl).match(/\/collections\/([^/?#]+)/);
        if (match && match[1]) {
            const handle = match[1];
            const parent = String(parentHandle || '').trim();
            return parent && parent.toLowerCase() !== handle.toLowerCase() ? '/' + parent + '/' + handle : '/' + handle;
        }
        return rawUrl;
    }

    function transformShopifyItems(items, rootParentHandle, isRoot) {
        const atRoot = isRoot !== false;
        return (items || []).map((item) => {
            let currentRootParent = rootParentHandle;
            if (atRoot && item.resource && item.resource.handle && item.type === 'COLLECTION') {
                currentRootParent = item.resource.handle;
            }
            return {
                ...item,
                url: transformMenuUrl(item.url, currentRootParent),
                items: transformShopifyItems(item.items || [], currentRootParent, false),
            };
        });
    }

    function shopifyItemToLink(item) {
        const url = cleanShopifyMenuUrl(item.url);
        const isExternal = isExternalSiteUrl(url);
        return {
            id: String(item.id || url),
            label: item.title || '',
            href: url || '#',
            target: isExternal ? '_blank' : '_self',
        };
    }

    /**
     * GlobalStore.js — level 1 = nav item, level 2 = tab, level 3 = tab links.
     * A level-2 item with no children becomes its own single link.
     */
    function transformShopifyMenu(menu) {
        const menuItems = ((menu && menu.items) || []).map((item) => {
            const tabs = (item.items || []).map((level2) => {
                const children = level2.items && level2.items.length ? level2.items : [level2];
                return {
                    id: String(level2.id),
                    title: level2.title || '',
                    links: children.map(shopifyItemToLink),
                };
            });
            return {
                id: String(item.id),
                title: item.title || '',
                href: tabs.length ? '#' : cleanShopifyMenuUrl(item.url) || '#',
                tabs: tabs,
            };
        });

        // No hardcoded ディスカバー entry: the Shopify main-menu already contains one,
        // and the old "append if missing" check compared against '/discover' while
        // Shopify returns menu URLs absolute and typed HTTP, so it never matched and
        // the nav rendered ディスカバー twice.
        return menuItems;
    }

    /* --------------------------------------------------------------------- *
     * Footer menus (server/utils/shopify.ts FOOTER_MENU_HANDLES)
     * --------------------------------------------------------------------- */

    const FOOTER_MENU_HANDLES = {
        footer: 'footer',
        footerNew: 'footer_new',
        footer3: 'footer3',
        sns: 'sns',
        contact: 'link-list',
        policy: 'policy2',
    };

    /**
     * Shopify prepends the store URL to links authored as bare "#" anchors,
     * yielding "https://store.myshopify.com#https://real-target". Recover the
     * real target and treat a bare store + "#" as a no-op. (GlobalStore.js)
     */
    function cleanShopifyMenuUrl(raw) {
        let url = String(raw || '').trim();
        if (!url) {
            return '#';
        }
        const embedded = url.match(/#(https?:\/\/.+)$/);
        if (embedded) {
            url = embedded[1];
        }
        if (/^https?:\/\/[^#]*#$/.test(url)) {
            return '#';
        }
        return sitePageUrl(url);
    }

    async function fetchShopifyFooterMenus() {
        const aliases = Object.keys(FOOTER_MENU_HANDLES)
            .map((key) => key + ': menu(handle: "' + FOOTER_MENU_HANDLES[key] + '") { ...FooterMenuFields }')
            .join(' ');
        const query =
            'query FooterMenus { ' +
            aliases +
            ' } fragment FooterMenuFields on Menu { handle title items { id title url type } }';
        return shopifyRequest(query, {});
    }

    function menuToFooterSection(menu) {
        if (!menu || !Array.isArray(menu.items) || !menu.items.length) {
            return null;
        }
        return {
            id: menu.handle,
            title: menu.title || '',
            items: menu.items.map(shopifyItemToLink),
        };
    }

    function buildFooter(footerMenus, decoration) {
        const details = (decoration && decoration.details) || {};
        // ext_2: landscape footer background (desktop). ext_6: portrait version for
        // below 1024px (GlobalStore.js does the same); falls back to the landscape image.
        const imageUrl = (details.ext_2 && details.ext_2.url) || null;
        const portraitImageUrl = (details.ext_6 && details.ext_6.url) || imageUrl;
        const columns = [
            [footerMenus && footerMenus.footer, footerMenus && footerMenus.footerNew],
            [footerMenus && footerMenus.footer3, footerMenus && footerMenus.sns],
            [footerMenus && footerMenus.contact],
        ]
            .map((group) => group.map(menuToFooterSection).filter(Boolean))
            .filter((group) => group.length > 0);

        return {
            columns: columns,
            mainMenu: columns.reduce((all, group) => all.concat(group), []),
            extraMenu: ((footerMenus && footerMenus.policy && footerMenus.policy.items) || []).map(shopifyItemToLink),
            textBeforeExtraMenu: 'Paul Smith. All right reserved',
            image: imageUrl
                ? { landscape: imageUrl, portrait: portraitImageUrl }
                : { landscape: null, portrait: null },
            quote: details.ext_1 || '',
            // Both come from the Storyblok "ps-footer-main" story, which this build
            // does not read; the live site currently has them disabled.
            showSignup: false,
            showCountrySwitcher: false,
            countryLabel: CONFIG.countryLabel,
            currencyLabel: CONFIG.currencyLabel,
            emailPlaceholder: T['Enter Your Email'],
        };
    }

    /* --------------------------------------------------------------------- *
     * Kuroco decoration + help FAQ (GlobalStore.js)
     * --------------------------------------------------------------------- */

    function isTruthy(value) {
        if (Array.isArray(value)) {
            return value.some((item) => isTruthy(item && item.key !== undefined ? item.key : item));
        }
        if (value && typeof value === 'object') {
            return isTruthy(value.key !== undefined ? value.key : value.value);
        }
        return value === true || value === 1 || value === '1' || String(value).toLowerCase() === 'true';
    }

    function extractStripe(decoration) {
        const url =
            (decoration && decoration.details && decoration.details.ext_5 && decoration.details.ext_5.url) || null;
        const src = url || asset(FALLBACK_STRIPE);
        return { desktop: src, mobile: src };
    }

    function buildHelpFaqItems(info) {
        const list = (info && info.list) || [];
        return list
            .filter((item) => isTruthy(item && item.open_flg) && isTruthy(item && item.ext_28))
            .sort((a, b) => Number((b && b.order_no) || 0) - Number((a && a.order_no) || 0))
            .map((item) => {
                let slug = item.slug && item.slug !== '' ? String(item.slug) : String(item.topics_id || '');
                slug = slug.replace(/\.html?$/i, '');
                return {
                    label: item.subject || '',
                    href: sitePageUrl(slug.charAt(0) === '/' ? '/information' + slug : '/information/' + slug),
                };
            });
    }

    /* --------------------------------------------------------------------- *
     * Bag count — Shopify Ajax Cart API (same source as the /cart page)
     * --------------------------------------------------------------------- */

    async function fetchCartCount() {
        try {
            const cart = await fetchJson('/cart.js', { headers: { Accept: 'application/json' } });
            return Number(cart && cart.item_count) || 0;
        } catch (error) {
            return 0;
        }
    }

    /* --------------------------------------------------------------------- *
     * Wishlist (stores/CustomerStore.js + utils/favorites.js)
     *
     * The Nuxt wishlist is plain localStorage (`ps_favorites_v1[:customerId]`),
     * hydrated through the Nuxt server route /api/favorites/products and synced
     * to Swym server-side for logged-in customers. Both front ends share the
     * kuroco-edge origin, so this reads the exact same favorites the SPA wrote
     * and calls the same routes — the tray shows identical items on both.
     * --------------------------------------------------------------------- */

    const FAVORITES_STORAGE_KEY = 'ps_favorites_v1';

    function shopifyNumericId(gid) {
        const match = String(gid || '').match(/\/(\d+)$/);
        return match ? match[1] : String(gid || '');
    }

    /**
     * Mirror of normalizeSkuSlug in utils/sku.ts (Nuxt repo). Favourites store the
     * full variant SKU, but /products/{slug} only resolves the colour-level form —
     * server/api/shopify/product-by-slug rejects a size suffix with a 404. Linking
     * the raw SKU here sent every tray item on this domain to a 404 while the same
     * item on the Nuxt domain, which already normalizes, worked.
     * Example: 1525005200011R____100S -> 1525005200011R____100
     */
    function normalizeSkuSlug(value) {
        const trimmed = String(value || '').trim();
        if (!trimmed) {
            return '';
        }
        // Fixed-width SKUs carry the colour in the first 21 characters.
        if (/^\d{10}[A-Za-z0-9_]{8}[A-Za-z0-9]{3}[A-Za-z0-9_+-]*$/.test(trimmed)) {
            return trimmed.slice(0, 21);
        }
        const separator = trimmed.indexOf('____') >= 0 ? '____' : trimmed.indexOf('__') >= 0 ? '__' : '';
        if (!separator) {
            return trimmed;
        }
        const parts = trimmed.split(separator);
        const productCode = parts[0];
        const colourCode = (String(parts[1] || '').match(/^[A-Za-z0-9]{3}/) || [])[0] || '';
        if (!productCode || !colourCode) {
            return trimmed;
        }
        return productCode + separator + colourCode;
    }

    /**
     * getFavoritesStorageKey(customer). Nuxt keys by the numeric customer id;
     * Shopify Liquid pages expose the same id as window.__st.cid. The email-hash
     * fallback is not reproduced — without a customer object there is nothing to
     * hash, and cid is always present on Liquid pages when logged in.
     */
    function favoritesStorageKey() {
        const cid =
            String(CONFIG.customerId || '') ||
            (isLoggedIn() && window.__st && window.__st.cid ? String(window.__st.cid) : '');
        return cid ? FAVORITES_STORAGE_KEY + ':' + cid : FAVORITES_STORAGE_KEY;
    }

    function normalizeFavoriteKey(value) {
        if (!value) {
            return '';
        }
        if (typeof value === 'string') {
            return value;
        }
        return (
            (value.favorite && (value.favorite.key || value.favorite.sku || value.favorite.variantId)) ||
            (value.selectedConfig && value.selectedConfig.sku) ||
            value.key ||
            value.sku ||
            value.variantId ||
            value.product ||
            value.uid ||
            ''
        );
    }

    function readFavorites(storageKey) {
        try {
            const raw = localStorage.getItem(storageKey || favoritesStorageKey());
            const favorites = raw ? JSON.parse(raw) : [];
            return Array.isArray(favorites) ? favorites : [];
        } catch (error) {
            return [];
        }
    }

    /** utils/favorites.js mergeFavoriteItems. */
    function mergeFavorites(collections) {
        const merged = new Map();
        collections.flat().forEach((favorite) => {
            const key = normalizeFavoriteKey(favorite);
            if (key) {
                merged.set(key, Object.assign({}, merged.get(key), favorite));
            }
        });
        return Array.from(merged.values());
    }

    /**
     * CustomerStore.syncWishlist() merges the guest list into the account list
     * after login. The extract merges for display; the Swym pull in
     * hydrateWishlist() is what reconciles across devices and domains.
     */
    function readMergedFavorites() {
        const accountKey = favoritesStorageKey();
        return mergeFavorites([
            readFavorites(accountKey),
            accountKey === FAVORITES_STORAGE_KEY ? [] : readFavorites(FAVORITES_STORAGE_KEY),
        ]);
    }

    function writeFavorites(favorites) {
        try {
            localStorage.setItem(favoritesStorageKey(), JSON.stringify(favorites));
        } catch (error) {
            /* storage full / private mode — the tray still works in memory */
        }
    }

    function favoritesApiRequest(path, payload) {
        const base = normalizeBaseUrl(CONFIG.favoritesApiBaseUrl);
        return fetchJson(base + path, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            // Cross-origin needs the Nuxt session cookie for the Swym route.
            credentials: base ? 'include' : 'same-origin',
            body: JSON.stringify(payload),
        });
    }

    /** CustomerStore.sendWishlistToSwym — Nuxt route, logged-in only. 'sync'
     * and 'list' are allowed with an empty list, like the store's version. */
    function sendWishlistToSwym(action, favorites) {
        const allowsEmpty = action === 'sync' || action === 'list';
        if (!isLoggedIn() || (!favorites.length && !allowsEmpty)) {
            return Promise.resolve(null);
        }
        return favoritesApiRequest('/api/favorites/swym', { action: action, favorites: favorites }).catch((error) => {
            console.warn('[ps-header-footer] Swym sync failed:', error);
            return null;
        });
    }

    /** CustomerStore.hydrateWishlistProducts, including its offline fallback. */
    function favoriteFallbackProduct(favorite) {
        return {
            product: favorite.key,
            uid: favorite.key,
            name: favorite.productName,
            uri: favorite.productUrl,
            sku: favorite.sku,
            price: favorite.price,
            priceAsNumber: favorite.priceAsNumber,
            thumbnailImage: { url: favorite.imageUrl, alt: favorite.imageAlt },
            items: [],
            favorite: favorite,
        };
    }

    async function hydrateWishlist() {
        let favorites = readMergedFavorites();
        // Instant paint from the stored favorites, then refresh from Shopify.
        if (!state.wishlist.hydrated) {
            state.wishlist.items = favorites.map(favoriteFallbackProduct);
        }

        // On this domain the bridge is the authority: syncWishlistBridge() has
        // reconciled the shopper's Swym list into ps_wl using the session only
        // this origin has. localStorage here is per-origin and effectively
        // always empty, so without this the tray showed nothing.
        await wishlistBridgeReady;
        const published = readCookie(WISHLIST_BRIDGE_COOKIE);

        if (published) {
            const localByKey = {};
            favorites.forEach(function (favorite) {
                localByKey[normalizeFavoriteKey(favorite)] = favorite;
            });
            favorites = decodeBridge(published, false).map(function (entry) {
                const favorite = bridgeEntryToFavorite(entry);
                return Object.assign(favorite, localByKey[normalizeFavoriteKey(favorite)]);
            });
            writeFavorites(favorites);
        } else if (isLoggedIn()) {
            // No bridge cookie. A Customer Account API session may still exist,
            // in which case the Nuxt route can answer; under legacy customer
            // accounts it 401s and this is a no-op.
            const response = await sendWishlistToSwym('sync', favorites);
            if (response && Array.isArray(response.favorites)) {
                favorites = mergeFavorites([response.favorites, favorites]);
                writeFavorites(favorites);
            }
        }

        if (!favorites.length) {
            state.wishlist.items = [];
            state.wishlist.hydrated = true;
            return;
        }
        try {
            const products = await favoritesApiRequest('/api/favorites/products', { favorites: favorites });
            state.wishlist.items = Array.isArray(products) ? products : favorites.map(favoriteFallbackProduct);
        } catch (error) {
            console.warn('[ps-header-footer] wishlist hydration failed:', error);
        }
        state.wishlist.hydrated = true;
    }

    async function removeFromWishlist(key, update) {
        const favorites = readFavorites(favoritesStorageKey());
        const favorite = favorites.find((stored) => normalizeFavoriteKey(stored) === key);
        writeFavorites(favorites.filter((stored) => normalizeFavoriteKey(stored) !== key));
        if (favorite) {
            // Swym first, with this origin's session; the Nuxt route is only a
            // fallback for a Customer Account API session, which legacy customer
            // accounts never produce.
            const removed = await swymRemoveFavorite(favorite);
            if (!removed) {
                await sendWishlistToSwym('remove', [favorite]);
            }
        }
        if (update !== false) {
            state.wishlist.items = state.wishlist.items.filter((item) => normalizeFavoriteKey(item) !== key);
        }
    }

    /* --------------------------------------------------------------------- *
     * Cross-domain wishlist bridge
     *
     * Mirror of the codec in utils/favorites.js (Nuxt repo). This side owns the
     * Swym writes: under legacy customer accounts only this domain has the
     * shopper's session, so the Nuxt app cannot prove to Swym who they are. It
     * queues intent in ps_wl_pending; we drain it through the Swym SDK and
     * publish the resulting list back in ps_wl. Both cookies are scoped to the
     * shared parent domain, like ps_cart_count in theme.liquid.
     * --------------------------------------------------------------------- */

    const WISHLIST_BRIDGE_COOKIE = 'ps_wl';
    const WISHLIST_PENDING_COOKIE = 'ps_wl_pending';
    const WISHLIST_BRIDGE_DOMAIN = '.paulsmith.co.jp';
    const WISHLIST_BRIDGE_MAX_ITEMS = 50;
    // "The Swym list is empty", as distinct from an absent cookie meaning "no
    // bridge". decodeWishlistBridge on the Nuxt side already yields [] for this,
    // so it needs no change there.
    const WISHLIST_BRIDGE_EMPTY = '-';
    // Resolved once syncWishlistBridge() has reconciled Swym into ps_wl, so the
    // tray can wait for it instead of racing it. Assigned at the bottom.
    let wishlistBridgeReady = Promise.resolve();

    function writeBridgeCookie(name, value) {
        const attrs = '; path=/; secure; samesite=lax; domain=' + WISHLIST_BRIDGE_DOMAIN;
        document.cookie = value
            ? name + '=' + encodeURIComponent(value) + '; max-age=1209600' + attrs
            : name + '=; max-age=0' + attrs;
    }

    function decodeBridge(value, withOp) {
        return String(value || '')
            .split('|')
            .map(function (record) {
                const parts = record.split('~');
                const op = withOp ? parts.shift() : '';
                if (!parts[0] || !parts[1] || (withOp && op !== 'a' && op !== 'd')) {
                    return null;
                }
                return { op: op, empi: parts[0], epi: parts[1], sku: parts[2] || '', handle: parts[3] || '' };
            })
            .filter(Boolean);
    }

    /** Swym listcontents -> the compact form the Nuxt side reads. */
    function encodeSwymList(items) {
        return (Array.isArray(items) ? items : [])
            .slice(0, WISHLIST_BRIDGE_MAX_ITEMS)
            .map(function (item) {
                const empi = String(item.empi || '');
                const epi = String(item.epi || '');
                if (!empi || !epi) {
                    return '';
                }
                const sku = String(item.sku || (item.cprops && item.cprops.sku) || '');
                return [empi, epi, sku, swymProductHandle(item)].join('~').replace(/[;,\s]/g, '');
            })
            .filter(Boolean)
            .join('|');
    }

    /** Same parse as c_main-customers-favorite-product.liquid's getProductHandle. */
    function swymProductHandle(item) {
        try {
            const parts = new URL(item.du, window.location.origin).pathname.split('/').filter(Boolean);
            const index = parts.indexOf('products');
            return index >= 0 ? parts[index + 1] || '' : '';
        } catch (error) {
            return '';
        }
    }

    function swymReady() {
        return new Promise(function (resolve) {
            window.SwymCallbacks = window.SwymCallbacks || [];
            // The app embed may be disabled on some templates; never hang.
            const timer = setTimeout(function () {
                resolve(null);
            }, 8000);
            window.SwymCallbacks.push(function (swat) {
                clearTimeout(timer);
                resolve(swat);
            });
        });
    }

    /**
     * Pick the same list the rest of the site does: the LAST wishlist-type list
     * Swym returns. c_main-customers-favorite-product.liquid and main-account
     * take `[...lists].reverse()[0]`, and server/utils/swym.ts now matches, so a
     * shopper with more than one list sees the same contents everywhere.
     */
    function swymFetchList(swat) {
        return new Promise(function (resolve) {
            if (typeof swat.fetchLists !== 'function') {
                return resolve(null);
            }
            swat.fetchLists({
                callbackFn: function (lists) {
                    const wishlists = (Array.isArray(lists) ? lists : []).filter(function (list) {
                        return !list.lty || list.lty === 'wl';
                    });
                    resolve(wishlists[wishlists.length - 1] || null);
                },
                errorFn: function () {
                    resolve(null);
                },
            });
        });
    }

    /**
     * Create the shopper's wishlist. fetchLists returns [] for anyone who has
     * never used the storefront wishlist, and without this the bridge would
     * idle forever for them. Matches createWishlist() in server/utils/swym.ts.
     */
    function swymCreateList(swat) {
        return new Promise(function (resolve) {
            if (typeof swat.createList !== 'function') {
                return resolve(null);
            }
            swat.createList(
                { lname: 'My Wishlist', lty: 'wl' },
                function (list) {
                    resolve(list && list.lid ? list : null);
                },
                function () {
                    resolve(null);
                },
            );
        });
    }

    /**
     * Batch API where available, else fold the single-product one over the list.
     * Names verified against the Swym build on this store: the removal methods
     * are remove*, not delete* — the delete* names are kept as fallbacks only
     * because other Swym builds have used them.
     */
    function swymApply(swat, lid, op, products) {
        if (!products.length) {
            return Promise.resolve(true);
        }

        const batch = op === 'a' ? swat.addProductsToList : swat.removeProductsFromList || swat.deleteProductsFromList;
        if (typeof batch === 'function') {
            return new Promise(function (resolve) {
                batch.call(
                    swat,
                    lid,
                    products,
                    function () {
                        resolve(true);
                    },
                    function () {
                        resolve(false);
                    },
                );
            });
        }

        const single = op === 'a' ? swat.addToWishList : swat.removeFromWishList || swat.deleteFromWishList;
        if (typeof single !== 'function') {
            console.warn('[ps-header-footer] Swym SDK exposes no add/remove method; wishlist bridge idle.');
            return Promise.resolve(false);
        }

        return products.reduce(function (chain, product) {
            return chain.then(function (ok) {
                return new Promise(function (resolve) {
                    single.call(
                        swat,
                        product,
                        function () {
                            resolve(ok);
                        },
                        function () {
                            resolve(false);
                        },
                    );
                });
            });
        }, Promise.resolve(true));
    }

    /** Mirror of bridgeEntryToFavorite in utils/favorites.js. */
    function bridgeEntryToFavorite(entry) {
        const variantId = 'gid://shopify/ProductVariant/' + entry.epi;
        return {
            key: entry.sku || variantId,
            sku: entry.sku || '',
            variantId: variantId,
            variantNumericId: entry.epi,
            productId: 'gid://shopify/Product/' + entry.empi,
            productNumericId: entry.empi,
            productHandle: entry.handle,
            // Tray links point at the Nuxt site, like every other link here.
            productUrl: entry.handle ? SITE_PAGE_BASE_URL + '/products/' + entry.handle : '',
            quantity: 1,
        };
    }

    function bridgeEntryToSwymProduct(entry) {
        return {
            empi: Number(entry.empi),
            epi: Number(entry.epi),
            du: shopifyPageUrl('/products/' + entry.handle),
            qty: 1,
        };
    }

    function favoriteToSwymProduct(favorite) {
        const handle = favorite.productHandle || ((favorite.productUrl || '').match(/\/products\/([^/?#]+)/) || [])[1];
        return {
            empi: Number(favorite.productNumericId || shopifyNumericId(favorite.productId)),
            epi: Number(favorite.variantNumericId || shopifyNumericId(favorite.variantId)),
            du: handle ? shopifyPageUrl('/products/' + handle) : favorite.productUrl || '',
            qty: 1,
        };
    }

    /**
     * Republish ps_wl from Swym after a mutation. Debounced because "Remove all"
     * and "Add all to bag" fire one removal per item and would otherwise refetch
     * the list once each.
     */
    let publishBridgeTimer = null;
    function scheduleBridgePublish(swat) {
        clearTimeout(publishBridgeTimer);
        publishBridgeTimer = setTimeout(function () {
            swymFetchList(swat).then(function (list) {
                // The sentinel distinguishes "Swym list is empty" from "no bridge
                // at all". Without it the cookie would be deleted, the Nuxt side
                // would fall back to its own localStorage, and a removal made
                // here would be undone on that domain.
                writeBridgeCookie(
                    WISHLIST_BRIDGE_COOKIE,
                    (list && encodeSwymList(list.listcontents)) || WISHLIST_BRIDGE_EMPTY,
                );
            });
        }, 400);
    }

    /**
     * Remove from Swym using this origin's session. The Nuxt route cannot do it
     * under legacy customer accounts — it 401s — so before this, removals made
     * on the storefront reached neither Swym nor the Nuxt domain, and came back
     * on the next page load when the bridge republished from Swym.
     */
    async function swymRemoveFavorite(favorite) {
        if (!isLoggedIn()) {
            return false;
        }

        const swat = await swymReady();
        if (!swat) {
            return false;
        }

        const list = await swymFetchList(swat);
        if (!list || !list.lid) {
            return false;
        }

        const product = favoriteToSwymProduct(favorite);
        if (!product.empi || !product.epi) {
            return false;
        }

        const removed = await swymApply(swat, list.lid, 'd', [product]);
        if (removed) {
            scheduleBridgePublish(swat);
        }
        return removed;
    }

    /**
     * Drain whatever the Nuxt side queued, then publish the resulting Swym list
     * back to it. Runs on every storefront page load for a logged-in shopper —
     * cart, account, login, checkout — which is when the two sides converge.
     */
    async function syncWishlistBridge() {
        if (!isLoggedIn()) {
            return;
        }

        const swat = await swymReady();
        if (!swat) {
            return;
        }

        const pending = decodeBridge(readCookie(WISHLIST_PENDING_COOKIE), true);
        let list = await swymFetchList(swat);

        // Nothing to sync into and nothing queued: leave Swym alone rather than
        // creating an empty list for every shopper who loads a storefront page.
        if ((!list || !list.lid) && pending.some((entry) => entry.op === 'a')) {
            list = await swymCreateList(swat);
        }
        if (!list || !list.lid) {
            return;
        }

        if (pending.length) {
            const productsFor = function (op) {
                return pending
                    .filter(function (entry) {
                        return entry.op === op && entry.handle;
                    })
                    .map(bridgeEntryToSwymProduct);
            };

            const added = await swymApply(swat, list.lid, 'a', productsFor('a'));
            const removed = await swymApply(swat, list.lid, 'd', productsFor('d'));

            // Only clear once both applied, or a transient Swym failure would
            // silently drop the shopper's favourites.
            if (added && removed) {
                writeBridgeCookie(WISHLIST_PENDING_COOKIE, '');
            }
            list = (await swymFetchList(swat)) || list;
        }

        // Sentinel rather than deleting the cookie when the list is empty: an
        // absent cookie means "no bridge" and sends the Nuxt side back to its
        // own localStorage, which would resurrect whatever was just removed —
        // here, in Swym's own UI, or on another device. A list that exists and
        // is empty is a deliberate state; a shopper who has never used the
        // wishlist has no list at all and returns above without publishing.
        writeBridgeCookie(WISHLIST_BRIDGE_COOKIE, encodeSwymList(list.listcontents) || WISHLIST_BRIDGE_EMPTY);
    }

    function favoriteStockState(item) {
        return (item && item.favorite && item.favorite.stockState) || 'available';
    }

    function isFavoriteAvailable(item) {
        // WLMB.vue + WishlistItem.vue: members-only products are also blocked.
        return favoriteStockState(item) === 'available' && item.member_only_product_value !== '1';
    }

    async function refreshCartCount() {
        const count = await fetchCartCount();
        state.data.header.cartCount = count;
        document.querySelectorAll('[data-cart-count]').forEach((el) => {
            el.textContent = String(count);
        });
    }

    /** WLMB.vue moveToBag — Shopify Ajax Cart, the same cart the SPA writes to. */
    async function addFavoriteToBag(item) {
        const favorite = item.favorite || {};
        const variantId = shopifyNumericId(favorite.variantId || (favorite.selectedConfig || {}).value);
        if (!variantId || !isFavoriteAvailable(item)) {
            return false;
        }
        try {
            await fetchJson('/cart/add.js', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ items: [{ id: Number(variantId), quantity: 1 }] }),
            });
        } catch (error) {
            console.warn('[ps-header-footer] add to bag failed:', error);
            return false;
        }
        await refreshCartCount();
        return true;
    }

    /* --------------------------------------------------------------------- *
     * Data
     * --------------------------------------------------------------------- */

    async function fetchData() {
        const warn = (label) => (error) => {
            console.warn('[ps-header-footer] ' + label + ' unavailable:', error);
            return null;
        };

        const [mainMenu, footerMenus, decoration, info, cartCount] = await Promise.all([
            fetchShopifyMenu('main-menu').catch(warn('Shopify main-menu')),
            fetchShopifyFooterMenus().catch(warn('Shopify footer menus')),
            kurocoFetch('/layout_decoration/9858').catch(warn('Kuroco layout_decoration')),
            kurocoFetch('/info').catch(warn('Kuroco info')),
            fetchCartCount(),
        ]);

        return {
            stripe: extractStripe(decoration),
            header: {
                menu: mainMenu ? transformShopifyMenu(mainMenu) : [],
                cartCount: cartCount,
            },
            footer: buildFooter(footerMenus, decoration),
            helpFaqItems: buildHelpFaqItems(info),
        };
    }

    /* --------------------------------------------------------------------- *
     * Header markup (PsHeader.vue)
     * --------------------------------------------------------------------- */

    function renderLogo(className) {
        return (
            '<a class="ps-logo' +
            (className ? ' ' + className : '') +
            '" href="' +
            escapeHtml(sitePageUrl('/')) +
            '" title="Paul Smith" aria-label="Paul Smith">' +
            LOGO +
            '</a>'
        );
    }

    function renderStripe(stripe) {
        const desktop = (stripe && stripe.desktop) || '';
        const mobile = (stripe && stripe.mobile) || desktop;
        const image = desktop
            ? '<div class="ps-stripe-inner"><picture><source media="(max-width: 1023px)" srcset="' +
              escapeHtml(mobile) +
              '"><img src="' +
              escapeHtml(desktop) +
              '" alt=""></picture></div>'
            : '';
        return '<div class="ps-stripe" data-header-hide>' + image + '</div>';
    }

    function renderNavItem(item, className) {
        const cls = 'ps-nav-item' + (className ? ' ' + className : '');
        if (item.tabs.length) {
            return (
                '<button class="' +
                cls +
                '" type="button" data-menu-id="' +
                escapeHtml(item.id) +
                '">' +
                escapeHtml(item.title) +
                '</button>'
            );
        }
        return '<a class="' + cls + '" href="' + escapeHtml(item.href) + '">' + escapeHtml(item.title) + '</a>';
    }

    function label(text, withLabels) {
        return withLabels ? '<span class="ps-action-label">' + escapeHtml(text) + '</span>' : '';
    }

    /**
     * HeaderRightSideItem.vue. `withLabels` mirrors Nuxt passing `:label` on the
     * >=1440px container only; the compact container renders icons alone.
     */
    function renderActions(data, withLabels) {
        const count = data.header.cartCount;
        const loggedIn = isLoggedIn();

        const search =
            '<div class="ps-action-search">' +
            '<div class="ps-search-inline">' +
            '<div class="ps-search-inline-field">' +
            '<div class="ps-search-inline-spacer"></div>' +
            '<div class="ps-search-inline-control">' +
            '<input type="text" placeholder="' +
            escapeHtml(T.Search) +
            '" autocomplete="off" aria-label="' +
            escapeHtml(T.Search) +
            '" data-inline-search>' +
            icon('search') +
            '</div></div></div>' +
            '<button class="ps-action ps-search-icon-button" type="button" aria-label="' +
            escapeHtml(T.Search) +
            '" data-panel="search">' +
            icon('new-search') +
            '</button>' +
            '</div>';

        const stores =
            '<a class="ps-action" href="' +
            escapeHtml(sitePageUrl('/shop-locator')) +
            '" aria-label="' +
            escapeHtml(T.Stores) +
            '">' +
            label(T.Stores, withLabels) +
            icon('open-doors') +
            '</a>';

        const help =
            '<button class="ps-action ps-action-help" type="button" aria-label="' +
            escapeHtml(T.Help) +
            '" data-panel="help">' +
            label(T.Help, withLabels) +
            icon('new-chat') +
            '</button>';

        const login =
            '<a class="ps-action" href="' +
            escapeHtml(accountUrl()) +
            '" aria-label="' +
            escapeHtml(loggedIn ? T.Account : T.Login) +
            '">' +
            label(loggedIn ? T.Account : T.Login, withLabels) +
            '<span class="ps-action-login-box">' +
            icon('new-account') +
            '</span></a>';

        // Opens the same custom wishlist tray as Nuxt (trays/Wishlist.vue),
        // NOT the Swym storefront drawer — deliberately no `c_wishlist-icon`
        // class or `#swym-wishlist` href, or Swym/c_header.js would bind to it
        // and open Swym's own UI over ours.
        const wishlist =
            '<button class="ps-action" type="button" aria-label="' +
            escapeHtml(T.Wishlist) +
            '" data-panel="wishlist">' +
            label(T.Wishlist, withLabels) +
            icon('wishlist') +
            '</button>';

        const bag =
            '<a class="ps-action ps-action-bag" href="' +
            escapeHtml(shopifyPageUrl('/cart')) +
            '" aria-label="' +
            escapeHtml(T.Bag) +
            '">' +
            label(T.Bag, withLabels) +
            '<span class="ps-action-bag-inner">' +
            icon('new-bag') +
            '<span class="ps-action-bag-label">' +
            escapeHtml(T.Bag) +
            '</span>' +
            '<span class="ps-action-count" data-cart-count>' +
            escapeHtml(count) +
            '</span>' +
            '</span></a>';

        return search + stores + help + login + wishlist + bag;
    }

    function renderHeader(data) {
        const menu = data.header.menu;
        const navItems = menu.map((item) => '<li>' + renderNavItem(item) + '</li>').join('');

        const headerBottom = menu.length
            ? '<ul class="ps-nav-bottom ps-hide-scrollbar" aria-label="Mobile primary" data-header-hide>' +
              menu.map((item) => '<li>' + renderNavItem(item) + '</li>').join('') +
              '</ul>'
            : '<div class="ps-nav-bottom-spacer" aria-hidden="true"></div>';

        return (
            renderStripe(data.stripe) +
            '<header class="ps-site-header" data-header-hide>' +
            '<div class="ps-header-top-row">' +
            renderLogo() +
            '<div class="ps-nav-side">' +
            '<nav class="ps-primary-nav" aria-label="Primary">' +
            '<ul class="ps-nav-list ps-hide-scrollbar">' +
            navItems +
            '</ul>' +
            '</nav>' +
            '</div>' +
            '<div class="ps-actions-desktop">' +
            renderActions(data, true) +
            '</div>' +
            '<div class="ps-actions-compact">' +
            '<div class="ps-actions-compact-inner">' +
            renderActions(data, false) +
            '<button class="ps-menu-toggle" type="button" aria-label="Menu" aria-expanded="false" data-menu-toggle>' +
            icon('new-menu') +
            '</button>' +
            '</div></div>' +
            '</div>' +
            '</header>' +
            '<div class="ps-header-background" data-header-hide></div>' +
            headerBottom +
            '<div class="ps-mega-menu ps-hide-scrollbar" data-mega-menu>' +
            '<div class="ps-mega-stripe-cover"></div>' +
            '<aside class="ps-mega-panel" data-mega-body></aside>' +
            '</div>' +
            '<div class="ps-mega-back" data-mega-back hidden></div>' +
            '<div class="ps-overlay" data-overlay></div>' +
            '<aside class="ps-side-panel" data-side-panel aria-live="polite"></aside>'
        );
    }

    /* --------------------------------------------------------------------- *
     * Mega menu (PsMegaMenu.vue)
     * --------------------------------------------------------------------- */

    function renderMegaTabbed(item, activeTabId) {
        const activeTab = item.tabs.find((tab) => tab.id === activeTabId) || item.tabs[0];
        const tabs = item.tabs.length
            ? '<div class="ps-mega-tabs ps-hide-scrollbar">' +
              item.tabs
                  .map(
                      (tab) =>
                          '<button class="ps-mega-tab' +
                          (activeTab && tab.id === activeTab.id ? ' is-active' : '') +
                          '" type="button" data-tab-id="' +
                          escapeHtml(tab.id) +
                          '">' +
                          escapeHtml(tab.title) +
                          '</button>',
                  )
                  .join('') +
              '</div>'
            : '';

        const links = activeTab
            ? '<div class="ps-mega-content"><div class="ps-mega-list"><div class="ps-mega-links">' +
              activeTab.links
                  .map(
                      (link) =>
                          '<a href="' +
                          escapeHtml(link.href) +
                          '" target="' +
                          escapeHtml(link.target || '_self') +
                          '">' +
                          escapeHtml(link.label) +
                          '</a>',
                  )
                  .join('') +
              '</div></div></div>'
            : '';

        return '<div class="ps-mega-body ps-hide-scrollbar">' + tabs + links + '</div>';
    }

    function renderMegaOffcanvas(data) {
        const items = data.header.menu
            .map((item) => {
                if (item.tabs.length) {
                    return (
                        '<button class="ps-offcanvas-item" type="button" data-menu-id="' +
                        escapeHtml(item.id) +
                        '">' +
                        escapeHtml(item.title) +
                        '</button>'
                    );
                }
                return (
                    '<a class="ps-offcanvas-item" href="' +
                    escapeHtml(item.href) +
                    '">' +
                    escapeHtml(item.title) +
                    '</a>'
                );
            })
            .join('');

        const links =
            '<a href="' +
            escapeHtml(accountUrl()) +
            '">' +
            escapeHtml(T.Account) +
            '</a>' +
            '<button type="button" data-panel="wishlist">' +
            escapeHtml(T.Wishlist) +
            '</button>' +
            '<button type="button" data-panel="help">' +
            escapeHtml(T.Help) +
            '</button>' +
            '<button type="button" data-panel="country">' +
            escapeHtml(CONFIG.currencyDisplayLabel) +
            '</button>';

        return (
            '<div class="ps-mega-offcanvas">' +
            '<div class="ps-offcanvas-items">' +
            items +
            '</div>' +
            '<div class="ps-offcanvas-links">' +
            links +
            '</div>' +
            // Empty promo-block column; PsMegaMenu.vue always renders it, and it
            // contributes one 48px row gap to the panel height.
            '<div class="ps-offcanvas-promo"></div>' +
            '</div>'
        );
    }

    /* --------------------------------------------------------------------- *
     * Side panels
     * --------------------------------------------------------------------- */

    /**
     * WishlistItem.vue. `item` is a hydrated product; `item.favorite` carries the
     * stored favorite (key, colour, size, stock state, variant).
     */
    function renderWishlistItem(item) {
        const favorite = item.favorite || {};
        const key = normalizeFavoriteKey(item);
        const status = state.wishlist.status[key];

        // itemStatus === 'added': grey banner + "View bag" instead of the card.
        if (status === 'added') {
            return (
                '<div class="ps-wishlist-added">' +
                '<p>' +
                escapeHtml(T['{product} was added to your bag'].replace('{product}', item.name || '')) +
                '</p>' +
                '<a class="ps-anchor-link" href="' +
                escapeHtml(shopifyPageUrl('/cart')) +
                '">' +
                escapeHtml(T['View bag']) +
                '</a>' +
                '</div>'
            );
        }

        const thumbnailUrl = (item.thumbnailImage && item.thumbnailImage.url) || favorite.imageUrl || '';
        // Same rule as WishlistItem.vue: build the PDP link from the colour-level
        // SKU, falling back to the stored uri when there is no SKU to normalize.
        // The favourite's SKU wins over the entity's: mapShopifyProductToEntity
        // sets entity.sku to the product's FIRST variant, not the favourited one,
        // so reading item.sku first linked the base colour for every favourite of
        // any other colour (1525035600026R____150 for a ____480 favourite).
        const itemSku = favorite.sku || item.sku || '';
        const productHref = sitePageUrl(
            itemSku ? '/products/' + encodeURIComponent(normalizeSkuSlug(itemSku) || itemSku) : item.uri || '#',
        );
        const image = thumbnailUrl
            ? '<img src="' + escapeHtml(thumbnailUrl) + '" alt="' + escapeHtml(item.name || '') + '" loading="lazy">'
            : '<span class="ps-wishlist-thumb-empty"></span>';
        // No price line: the live Nuxt tray renders the item as name ->
        // colour/size -> add-to-bag, with no price between them.
        const details = [favorite.color, favorite.size].filter(Boolean).join(' / ');

        // WishlistItem's hardcoded English status labels (not run through $t).
        const stockState = favoriteStockState(item);
        const unavailableLabel =
            stockState === 'discontinued'
                ? 'Discontinued'
                : stockState === 'restock'
                  ? 'Restock Notification'
                  : item.member_only_product_value === '1'
                    ? 'Members Only'
                    : '';
        const action = !unavailableLabel
            ? '<button class="ps-anchor-link" type="button" data-wishlist-add="' +
              escapeHtml(key) +
              '">' +
              escapeHtml(T['Add to Bag']) +
              '</button>'
            : '<p class="ps-wishlist-details">' + escapeHtml(unavailableLabel) + '</p>';

        return (
            '<div class="ps-wishlist-item">' +
            '<a class="ps-wishlist-thumb" href="' +
            escapeHtml(productHref) +
            '" title="' +
            escapeHtml(item.name || '') +
            '">' +
            image +
            '</a>' +
            '<div class="ps-wishlist-body">' +
            '<div class="ps-wishlist-top">' +
            '<div class="ps-wishlist-info">' +
            '<a href="' +
            escapeHtml(productHref) +
            '"><p class="ps-wishlist-name">' +
            escapeHtml(item.name || '') +
            '</p></a>' +
            (details ? '<p class="ps-wishlist-details">' + escapeHtml(details) + '</p>' : '') +
            '</div>' +
            '<button class="ps-wishlist-remove' +
            (status === 'removing' ? ' is-removing' : '') +
            '" type="button" aria-label="Remove" data-wishlist-remove="' +
            escapeHtml(key) +
            '">' +
            icon(status === 'removing' ? 'progress' : 'new-trash') +
            '</button>' +
            '</div>' +
            action +
            '</div>' +
            '</div>'
        );
    }

    /** trays/Wishlist.vue + WLMB.vue footer. */
    function renderWishlistPanel() {
        const items = state.wishlist.items;
        const body = items.length
            ? '<div class="ps-wishlist-items">' + items.map(renderWishlistItem).join('') + '</div>'
            : '<h5 class="ps-wishlist-empty">' + escapeHtml(T['Your wishlist is empty']) + '</h5>';

        // "Add all to bag / Remove all" appears from two items up (WLMB.vue).
        const footer =
            items.length > 1
                ? '<div class="ps-wishlist-footer">' +
                  '<button class="ps-anchor-link ps-anchor-link--small" type="button" data-wishlist-add-all>' +
                  escapeHtml(T['Add all to bag']) +
                  '</button>' +
                  '<button class="ps-anchor-link ps-anchor-link--small" type="button" data-wishlist-remove-all>' +
                  escapeHtml(T['Remove all']) +
                  '</button>' +
                  '</div>'
                : '';

        return (
            '<div class="ps-wishlist-tray">' +
            '<div class="ps-wishlist-scroll ps-hide-scrollbar" data-wishlist-body>' +
            body +
            '</div>' +
            footer +
            '</div>'
        );
    }

    function rerenderWishlistPanel() {
        if (state.activePanel !== 'wishlist') {
            return;
        }
        const sidePanel = headerRoot.querySelector('[data-side-panel]');
        if (sidePanel) {
            sidePanel.innerHTML = renderWishlistPanel();
        }
    }

    function renderSidePanel(name, data) {
        if (name === 'wishlist') {
            return renderWishlistPanel();
        }
        if (name === 'search') {
            return (
                '<div class="ps-side-panel-inner">' +
                '<form class="ps-search-form" data-search-form>' +
                icon('search') +
                '<input name="q" type="text" placeholder="' +
                escapeHtml(T['Type to search ...']) +
                '" autocomplete="off" data-search-input>' +
                '</form>' +
                '<div class="ps-search-results ps-hide-scrollbar" data-search-results></div>' +
                '</div>'
            );
        }
        if (name === 'help') {
            const items = data.helpFaqItems.length
                ? data.helpFaqItems
                : // Only used when Kuroco /info is unreachable. /information/faq is not
                  // a route (it 404s), so it is not offered here.
                  [
                      { label: 'お問い合わせ', href: '/information/contact' },
                      { label: 'ご利用ガイド', href: '/information/guide' },
                  ];
            return (
                '<div class="ps-side-panel-inner">' +
                '<h2 class="ps-panel-title">' +
                escapeHtml(T.Help) +
                '</h2>' +
                '<div class="ps-panel-links">' +
                items
                    .map(
                        (item) =>
                            '<a href="' + escapeHtml(sitePageUrl(item.href)) + '">' + escapeHtml(item.label) + '</a>',
                    )
                    .join('') +
                '</div></div>'
            );
        }
        return '';
    }

    /* --------------------------------------------------------------------- *
     * Footer (PsFooter.vue)
     * --------------------------------------------------------------------- */

    function renderFooterSection(section) {
        return (
            '<div class="ps-footer-menu">' +
            section.items
                .map(
                    (item) =>
                        '<a href="' +
                        escapeHtml(item.href) +
                        '" target="' +
                        escapeHtml(item.target || '_self') +
                        '">' +
                        escapeHtml(item.label) +
                        '</a>',
                )
                .join('') +
            '</div>'
        );
    }

    // PsFooter.vue (mobile) wraps each Shopify footer menu in a NewAccordion: the
    // menu title as the toggle, a chevron that flips, and the link list below.
    function renderFooterAccordion(section) {
        return (
            '<div class="ps-footer-accordion" data-footer-accordion>' +
            '<button type="button" class="ps-footer-accordion-toggle" aria-expanded="false" data-footer-accordion-toggle>' +
            '<span class="ps-footer-accordion-title">' +
            escapeHtml(section.title) +
            '</span>' +
            icon('new-arrow-small', 'ps-footer-accordion-icon') +
            '</button>' +
            '<div class="ps-footer-accordion-panel">' +
            '<div class="ps-footer-accordion-body">' +
            renderFooterSection(section) +
            '</div>' +
            '</div>' +
            '</div>'
        );
    }

    function renderFooterContact(footer) {
        const country = footer.showCountrySwitcher
            ? '<button class="ps-footer-country" type="button" data-panel="country">' +
              escapeHtml(footer.countryLabel) +
              '<span class="ps-footer-currency">(' +
              escapeHtml(footer.currencyLabel) +
              ')</span></button>'
            : '';
        return (
            '<div class="ps-footer-contact">' +
            country +
            '<a class="ps-footer-contact-item" href="' +
            escapeHtml(accountUrl()) +
            '" aria-label="' +
            escapeHtml(T.Account) +
            '">' +
            icon('new-account') +
            '<span class="ps-action-label">' +
            escapeHtml(T.Account) +
            '</span></a>' +
            '<button class="ps-footer-contact-item" type="button" aria-label="' +
            escapeHtml(T.Help) +
            '" data-panel="help">' +
            icon('new-chat') +
            '<span class="ps-action-label">' +
            escapeHtml(T.Help) +
            '</span></button>' +
            '</div>'
        );
    }

    function renderFooterBrand(footer) {
        return (
            '<div class="ps-footer-brand">' +
            (footer.quote ? '<div class="ps-footer-quote">' + escapeHtml(footer.quote) + '</div>' : '') +
            renderLogo() +
            '</div>'
        );
    }

    function renderNewsletter(footer) {
        if (!footer.showSignup) {
            return '';
        }
        return (
            '<form class="ps-newsletter" data-newsletter novalidate>' +
            '<label class="ps-sr-only" for="ps-newsletter-email">Email</label>' +
            '<input id="ps-newsletter-email" name="email" type="email" placeholder="' +
            escapeHtml(footer.emailPlaceholder) +
            '">' +
            '<button type="submit" aria-label="Submit">' +
            icon('new-arrow') +
            '</button>' +
            '</form>' +
            '<p class="ps-newsletter-message" data-newsletter-message></p>'
        );
    }

    function renderFooterLegal(footer) {
        const links = footer.extraMenu
            .map((link) =>
                link.href && link.href !== '#'
                    ? '<a href="' +
                      escapeHtml(link.href) +
                      '" target="' +
                      escapeHtml(link.target || '_self') +
                      '">' +
                      escapeHtml(link.label) +
                      '</a>'
                    : '<span>' + escapeHtml(link.label) + '</span>',
            )
            .join('');
        return (
            '<div class="ps-footer-legal">' + '<p>' + escapeHtml(footer.textBeforeExtraMenu) + '</p>' + links + '</div>'
        );
    }

    function renderFooter(data) {
        const footer = data.footer;
        const landscape = footer.image.landscape || '';
        const portrait = footer.image.portrait || landscape;
        // PsFooter.vue: landscape at >= 1024px, portrait below. Both are CSS
        // backgrounds (a display: none background is never downloaded, unlike a
        // hidden <img>), and the portrait one is sized to the viewport (auto 150vh,
        // repeated vertically) instead of covering the footer, which is taller than
        // 150vh here and would otherwise zoom the stripes far more than paulsmith.com.
        const bg = landscape
            ? '<div class="ps-footer-image" role="img" aria-label="Footer background" style="background-image: url(' +
              escapeHtml(landscape) +
              ')"></div>' +
              '<div class="ps-footer-image-m" role="img" aria-label="Footer background" style="background-image: url(' +
              escapeHtml(portrait) +
              ')"></div>'
            : '';

        const desktopColumns = footer.columns
            .map((group, index) => {
                const sections = group.map(renderFooterSection).join('');
                const contact = index === footer.columns.length - 1 ? renderFooterContact(footer) : '';
                return '<div class="ps-footer-column">' + sections + contact + '</div>';
            })
            .join('');

        return (
            '<footer class="ps-footer-main" data-footer>' +
            '<div class="ps-footer-bg">' +
            bg +
            '</div>' +
            // Collab media band — always reserved, matching PsFooter.vue.
            '<div class="ps-footer-collab" aria-hidden="true"></div>' +
            // --- below 1024px ---
            '<div class="ps-footer-content-m">' +
            '<div class="ps-footer-m-top">' +
            renderFooterBrand(footer) +
            renderNewsletter(footer) +
            '<div class="ps-footer-accordions">' +
            footer.mainMenu.map(renderFooterAccordion).join('') +
            '</div>' +
            renderFooterContact(footer) +
            '</div>' +
            '<div>' +
            renderFooterLegal(footer) +
            '</div>' +
            '</div>' +
            // --- 1024px and up ---
            '<div class="ps-footer-content-d">' +
            '<div class="ps-footer-col-brand">' +
            renderFooterBrand(footer) +
            '</div>' +
            '<div class="ps-footer-col-menus">' +
            '<div class="ps-footer-columns">' +
            desktopColumns +
            '</div>' +
            '</div>' +
            '<div class="ps-footer-col-signup">' +
            '<div class="ps-footer-signup">' +
            renderNewsletter(footer) +
            '</div>' +
            '</div>' +
            '<div class="ps-footer-col-legal">' +
            '<div class="ps-footer-legal-wrap">' +
            renderFooterLegal(footer) +
            '</div>' +
            '</div>' +
            '</div>' +
            '</footer>'
        );
    }

    /* --------------------------------------------------------------------- *
     * Interactive state
     * --------------------------------------------------------------------- */

    function isMenuOpen() {
        return state.activeMenuId !== null;
    }

    function closeAll() {
        state.activeMenuId = null;
        state.activeTabId = null;
        state.activePanel = null;
        currentSearchQuery = '';
        clearTimeout(searchDebounceTimer);
        sync();
    }

    function openMenu(id) {
        state.activePanel = null;
        if (state.activeMenuId === id) {
            state.activeMenuId = null;
            state.activeTabId = null;
        } else {
            state.activeMenuId = id;
            const item = state.data.header.menu.find((entry) => entry.id === id);
            state.activeTabId = item && item.tabs.length ? item.tabs[0].id : null;
        }
        sync();
    }

    function openPanel(name) {
        state.activeMenuId = null;
        state.activeTabId = null;
        state.activePanel = state.activePanel === name ? null : name;
        sync();
    }

    function sync() {
        const megaMenu = headerRoot.querySelector('[data-mega-menu]');
        const megaBody = headerRoot.querySelector('[data-mega-body]');
        const megaBack = headerRoot.querySelector('[data-mega-back]');
        const sidePanel = headerRoot.querySelector('[data-side-panel]');
        const overlay = headerRoot.querySelector('[data-overlay]');
        const toggle = headerRoot.querySelector('[data-menu-toggle]');
        const activeItem = state.data.header.menu.find((item) => item.id === state.activeMenuId);
        const offcanvas = state.activeMenuId === 'offcanvas';
        const open = isMenuOpen();

        if (megaBody) {
            megaBody.innerHTML = offcanvas
                ? renderMegaOffcanvas(state.data)
                : activeItem
                  ? renderMegaTabbed(activeItem, state.activeTabId)
                  : '';
        }
        if (megaMenu) {
            megaMenu.classList.toggle('is-open', open);
        }
        if (megaBack) {
            const showBack = Boolean(activeItem && !offcanvas);
            megaBack.hidden = !showBack;
            megaBack.innerHTML = showBack
                ? '<button type="button" data-mega-back-button>' +
                  icon('new-arrow') +
                  '<span class="ps-action-label">' +
                  escapeHtml(activeItem.title) +
                  '</span></button>'
                : '';
        }

        if (sidePanel) {
            sidePanel.innerHTML = state.activePanel ? renderSidePanel(state.activePanel, state.data) : '';
            sidePanel.classList.toggle('is-open', Boolean(state.activePanel));
            // The wishlist tray is wider than the search/help trays (GlobalTray's
            // `wlmb` variant) and needs its own paddings.
            sidePanel.classList.toggle('ps-side-panel--wishlist', state.activePanel === 'wishlist');
            if (state.activePanel === 'wishlist') {
                hydrateWishlist().then(rerenderWishlistPanel);
            }
            if (state.activePanel === 'search') {
                const input = sidePanel.querySelector('[data-search-input]');
                if (input) {
                    input.focus();
                    input.addEventListener('input', () => debouncedSearch(input.value.trim()));
                }
            }
        }

        if (overlay) {
            overlay.classList.toggle('is-open', Boolean(open || state.activePanel));
        }
        if (toggle) {
            toggle.innerHTML = icon(open ? 'new-close' : 'new-menu');
            toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
        }

        document.body.classList.toggle('ps-menu-open', Boolean(open || state.activePanel));

        // Nuxt dims every sibling while a tray or menu is open.
        headerRoot.querySelectorAll('.ps-nav-list > li').forEach((li) => {
            const button = li.querySelector('[data-menu-id]');
            const id = button && button.getAttribute('data-menu-id');
            li.classList.toggle('is-dimmed', Boolean((open || state.activePanel) && id !== state.activeMenuId));
            li.classList.toggle('is-active', Boolean(id && id === state.activeMenuId));
        });
        headerRoot.querySelectorAll('.ps-actions-desktop .ps-action').forEach((action) => {
            action.classList.toggle('is-dimmed', Boolean(open || state.activePanel));
        });

        // Below 1024px an open tray keeps the header in place (PsHeader.vue).
        updateFooterState();
    }

    /* --------------------------------------------------------------------- *
     * Zeta search (unchanged behaviour, restyled markup)
     * --------------------------------------------------------------------- */

    const ZETA_FACET_FIELDS = [
        'brand',
        'category',
        'size',
        'fit',
        'color',
        'pattern',
        'style',
        'collection',
        'price',
        'type',
        'item_stock',
        'material',
    ];

    let searchDebounceTimer = null;
    let currentSearchQuery = '';

    function buildZetaParams(query, page, limit) {
        const params = new URLSearchParams();
        if (query) {
            params.set('q', query);
        }
        params.set('l', String(limit));
        params.set('o', String((page - 1) * limit));
        params.set('c', ZETA_FACET_FIELDS.join(','));
        params.set('f', '0');
        params.set('stock', '1');
        params.set('color_info', '0');
        return params;
    }

    function getZetaAssetUrl(url) {
        if (!url) {
            return '';
        }
        if (/^https?:\/\//i.test(url)) {
            return url;
        }
        if (url.charAt(0) === '/') {
            return 'https://www.paulsmith.co.jp' + url;
        }
        return url;
    }

    function firstFilled() {
        for (let i = 0; i < arguments.length; i++) {
            const value = arguments[i];
            if (value !== undefined && value !== null && value !== '') {
                return String(value);
            }
        }
        return '';
    }

    function toNumber(value) {
        const n = Number(value);
        return Number.isFinite(n) ? n : 0;
    }

    function formatPrice(value) {
        const n = toNumber(value);
        if (CONFIG.currencyLabel === 'JPY') {
            return '¥' + n.toLocaleString('ja-JP');
        }
        return CONFIG.currencyLabel + ' ' + n.toLocaleString();
    }

    function formatZetaProduct(record) {
        // sale_price is the regular price; bargain_price (mirrored by
        // sort_sale_price) is the discounted selling price when on sale.
        const regularPrice = toNumber(record.fixed_price || record.original_price || record.sale_price || record.price);
        const salePrice = toNumber(record.bargain_price || record.sort_sale_price) || regularPrice;
        const originalPrice = regularPrice || salePrice;
        const sku = firstFilled(record.sku_cd, record.sku).split(';;;;')[0];

        return {
            name: firstFilled(record.item_nm, record.name),
            sku: sku,
            thumbnailUrl: getZetaAssetUrl(firstFilled(record.item_img_url, record.image_url, record.image)),
            uri: sku ? '/products/' + sku : '',
            price: formatPrice(salePrice),
            originalPrice: formatPrice(originalPrice),
            isOnSale: originalPrice > salePrice,
        };
    }

    async function zetaSearch(query, page, limit) {
        const params = buildZetaParams(query, page || 1, limit || 8);
        const endpoint = CONFIG.zetaApiEndpoint
            ? CONFIG.zetaApiEndpoint + '?' + params.toString()
            : CONFIG.zetaSearchProxyPath + '?' + params.toString();

        const response = await fetch(endpoint);
        if (!response.ok) {
            return { products: [], suggestions: [], total: 0 };
        }

        const data = await response.json();
        const payload = (data && data.result) || data || {};
        const records = payload.items || data.items || data.results || [];
        const attribute = payload.attribute || data.attribute || {};

        return {
            products: records.map(formatZetaProduct),
            suggestions: data.suggest || (data.result && data.result.suggest) || [],
            total: attribute.total || data.total || data.count || 0,
        };
    }

    function debounce(fn, delay) {
        return function () {
            const args = arguments;
            clearTimeout(searchDebounceTimer);
            searchDebounceTimer = setTimeout(() => fn.apply(null, args), delay);
        };
    }

    function renderSearchProduct(product) {
        const onSale = product.isOnSale
            ? '<span class="ps-search-product-original-price">' + escapeHtml(product.originalPrice) + '</span>'
            : '';
        const image = product.thumbnailUrl
            ? '<img class="ps-search-product-image" src="' +
              escapeHtml(product.thumbnailUrl) +
              '" alt="' +
              escapeHtml(product.name) +
              '" loading="lazy">'
            : '<div class="ps-search-product-image--empty"></div>';

        return (
            '<a class="ps-search-product" href="' +
            escapeHtml(sitePageUrl(product.uri || '#')) +
            '">' +
            '<div class="ps-search-product-info">' +
            '<p class="ps-search-product-name">' +
            escapeHtml(product.name) +
            '</p>' +
            '<div class="ps-search-product-price">' +
            escapeHtml(product.price) +
            onSale +
            '</div></div>' +
            '<div class="ps-search-product-thumb">' +
            image +
            '</div></a>'
        );
    }

    function renderSearchResults(results) {
        if (!results || !results.products.length) {
            return '<p class="ps-search-no-results">0 Results, Please try another search term.</p>';
        }

        const suggestions = (results.suggestions || [])
            .map((entry) => {
                const text = typeof entry === 'string' ? entry : entry && (entry.suggest || entry.term || entry.value);
                return text
                    ? '<button class="ps-search-suggestion" type="button" data-suggestion>' + text + '</button>'
                    : '';
            })
            .filter(Boolean)
            .join('');

        return (
            (suggestions ? '<div class="ps-search-suggestions">' + suggestions + '</div>' : '') +
            '<div class="ps-search-results-header">' +
            '<span class="ps-search-results-count">' +
            escapeHtml(results.total + ' Products') +
            '</span>' +
            '<a class="ps-search-view-all" href="' +
            escapeHtml(sitePageUrl('/search?q=' + encodeURIComponent(currentSearchQuery))) +
            '">View all</a>' +
            '</div>' +
            '<div class="ps-search-products-list">' +
            results.products.map(renderSearchProduct).join('') +
            '</div>'
        );
    }

    function updateSearchResults(results) {
        const container = headerRoot.querySelector('[data-search-results]');
        if (!container) {
            return;
        }
        container.innerHTML = results ? renderSearchResults(results) : '';
    }

    const debouncedSearch = debounce(async (query) => {
        if (query.length <= 2) {
            updateSearchResults(null);
            return;
        }

        currentSearchQuery = query;
        const container = headerRoot.querySelector('[data-search-results]');
        if (container) {
            container.innerHTML = '<p class="ps-search-loading">Searching...</p>';
        }

        try {
            const results = await zetaSearch(query, 1, 8);
            if (currentSearchQuery === query) {
                updateSearchResults(results);
            }
        } catch (error) {
            console.warn('[ps-header-footer] search failed:', error);
            if (currentSearchQuery === query) {
                updateSearchResults(null);
            }
        }
    }, 500);

    /* --------------------------------------------------------------------- *
     * Events
     * --------------------------------------------------------------------- */

    function goToSearch(query) {
        if (query) {
            window.location.href = sitePageUrl('/search?q=' + encodeURIComponent(query));
        }
    }

    function bindEvents() {
        document.addEventListener('click', (event) => {
            const target = event.target;
            const menuButton = target.closest('[data-menu-id]');
            const tabButton = target.closest('[data-tab-id]');
            const panelButton = target.closest('[data-panel]');
            const overlay = target.closest('[data-overlay]');
            const menuToggle = target.closest('[data-menu-toggle]');
            const backButton = target.closest('[data-mega-back-button]');
            const suggestion = target.closest('[data-suggestion]');
            const wishlistAdd = target.closest('[data-wishlist-add]');
            const wishlistRemove = target.closest('[data-wishlist-remove]');
            const wishlistAddAll = target.closest('[data-wishlist-add-all]');
            const wishlistRemoveAll = target.closest('[data-wishlist-remove-all]');

            if (wishlistAdd) {
                const key = wishlistAdd.getAttribute('data-wishlist-add');
                const item = state.wishlist.items.find((entry) => normalizeFavoriteKey(entry) === key);
                if (item) {
                    addFavoriteToBag(item).then((ok) => {
                        if (ok) {
                            state.wishlist.status[key] = 'added';
                            rerenderWishlistPanel();
                        }
                    });
                }
                return;
            }
            if (wishlistRemove) {
                const key = wishlistRemove.getAttribute('data-wishlist-remove');
                state.wishlist.status[key] = 'removing';
                rerenderWishlistPanel();
                removeFromWishlist(key).then(() => {
                    delete state.wishlist.status[key];
                    rerenderWishlistPanel();
                });
                return;
            }
            if (wishlistAddAll) {
                // WLMB.moveAllToQueue + moveQueueToBag: adds every available
                // favorite to the bag and removes those from the wishlist.
                const available = state.wishlist.items.filter(isFavoriteAvailable);
                Promise.all(
                    available.map((item) =>
                        addFavoriteToBag(item).then((ok) => {
                            if (ok) {
                                const key = normalizeFavoriteKey(item);
                                state.wishlist.status[key] = 'added';
                                return removeFromWishlist(key, false);
                            }
                            return null;
                        }),
                    ),
                ).then(rerenderWishlistPanel);
                return;
            }
            if (wishlistRemoveAll) {
                // CustomerStore.clearWishlist()
                const favorites = readFavorites(favoritesStorageKey());
                writeFavorites([]);
                sendWishlistToSwym('remove', favorites);
                state.wishlist.items = [];
                rerenderWishlistPanel();
                return;
            }

            if (menuButton) {
                openMenu(menuButton.getAttribute('data-menu-id'));
            } else if (tabButton) {
                state.activeTabId = tabButton.getAttribute('data-tab-id');
                sync();
            } else if (backButton) {
                state.activeMenuId = 'offcanvas';
                state.activeTabId = null;
                sync();
            } else if (panelButton) {
                openPanel(panelButton.getAttribute('data-panel'));
            } else if (overlay) {
                closeAll();
            } else if (menuToggle) {
                openMenu(isMenuOpen() ? state.activeMenuId : 'offcanvas');
            } else if (suggestion) {
                const text = suggestion.textContent.trim();
                const input = headerRoot.querySelector('[data-search-input]');
                if (input && text) {
                    input.value = text;
                    debouncedSearch(text);
                }
            }
        });

        document.addEventListener('submit', (event) => {
            const searchForm = event.target.closest('[data-search-form]');
            const newsletter = event.target.closest('[data-newsletter]');
            if (searchForm) {
                event.preventDefault();
                goToSearch(String(new FormData(searchForm).get('q') || '').trim());
            }
            if (newsletter) {
                event.preventDefault();
                const email = String(new FormData(newsletter).get('email') || '');
                const message = footerRoot.querySelector('[data-newsletter-message]');
                if (message) {
                    message.textContent = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)
                        ? 'Thank you.'
                        : 'Enter a valid email address.';
                }
            }
        });

        // The inline header field submits on Enter (SearchInput @on-enter).
        headerRoot.addEventListener('keydown', (event) => {
            if (event.key === 'Enter' && event.target.matches('[data-inline-search]')) {
                event.preventDefault();
                goToSearch(event.target.value.trim());
            }
        });

        document.addEventListener('keydown', (event) => {
            if (event.key === 'Escape') {
                closeAll();
            }
        });
    }

    /**
     * Mobile footer accordions. Like NewAccordion.vue with isMultiOpen=false: only
     * one section is open at a time. The height animates via grid-template-rows in
     * CSS, so the JS only toggles a class and aria-expanded.
     */
    function bindFooterAccordions() {
        footerRoot.addEventListener('click', (event) => {
            const toggle = event.target.closest('[data-footer-accordion-toggle]');
            if (!toggle) {
                return;
            }
            const item = toggle.closest('[data-footer-accordion]');
            const open = !item.classList.contains('is-open');
            footerRoot.querySelectorAll('[data-footer-accordion].is-open').forEach((other) => {
                other.classList.remove('is-open');
                other.querySelector('[data-footer-accordion-toggle]').setAttribute('aria-expanded', 'false');
            });
            item.classList.toggle('is-open', open);
            toggle.setAttribute('aria-expanded', String(open));
        });
    }

    // Re-runs the scroll-derived footer/header state; assigned once the footer
    // exists so sync() can refresh it when a tray opens.
    let updateFooterState = () => {};

    /**
     * PsFooter.vue parallaxes the footer background, and PsHeader.vue hides the
     * header once the footer is reached. Reproduced here so the footer behaves the
     * same without pulling in GSAP:
     *
     *   background   scale 1.1 -> 1, translateY -30% (mobile) / -50% -> 0
     *   mobile card  clip-path inset 0 -> 16px (padding stays 32px)
     *
     * The timeline uses ScrollTrigger start 'top bottom' / end 'bottom bottom', so
     * progress runs from "element enters the viewport" to "element bottom meets the
     * viewport bottom".
     */
    function bindFooterScrollEffects() {
        const footer = footerRoot.querySelector('[data-footer]');
        if (!footer) {
            return;
        }
        const images = footer.querySelectorAll('.ps-footer-image, .ps-footer-image-m');
        const mobileCard = footer.querySelector('.ps-footer-content-m');
        const hideTargets = headerRoot.querySelectorAll('[data-header-hide]');
        const desktop = window.matchMedia('(min-width: 1024px)');

        // Cached like ScrollTrigger's own refresh, so a resize is the only thing
        // that re-measures.
        let metrics = [];

        const measure = () => {
            metrics = [footer].map((el) => {
                const rect = el.getBoundingClientRect();
                return { el: el, top: rect.top + window.scrollY, height: rect.height || 1 };
            });
        };

        const progressOf = (el) => {
            const entry = metrics.find((item) => item.el === el);
            if (!entry) {
                return 0;
            }
            return Math.min(1, Math.max(0, (window.scrollY + window.innerHeight - entry.top) / entry.height));
        };

        const update = () => {
            const isDesktop = desktop.matches;
            const remaining = 1 - progressOf(footer);
            const shift = (isDesktop ? -50 : -30) * remaining;
            images.forEach((img) => {
                img.style.transform = 'translateY(' + shift + '%) scale(' + (1 + 0.1 * remaining) + ')';
            });

            // PsFooter.vue (mobile): card clip-path inset 0 -> 16px; padding stays 32px.
            if (mobileCard) {
                mobileCard.style.setProperty('--ps-footer-inset', 16 * (1 - remaining) + 'px');
            }

            // PsFooter.vue: isFooterReached = footer top - notificationBannerHeight
            // < 0, i.e. the footer's top edge has passed the viewport top — not
            // merely become visible. The banner is not part of this extract, so
            // its height counts as 0 (PsHeader.vue's isHiddenHeader also keeps the
            // header in place below 1024px while a tray is open).
            const reached = footer.getBoundingClientRect().top < 0;
            const hide = reached && (isDesktop || !(state.activePanel || isMenuOpen()));
            hideTargets.forEach((el) => el.classList.toggle('ps-header-hidden', hide));
        };

        const refresh = () => {
            measure();
            update();
        };

        updateFooterState = update;
        refresh();
        window.addEventListener('scroll', update, { passive: true });
        window.addEventListener('resize', refresh);
        if ('ResizeObserver' in window) {
            new ResizeObserver(refresh).observe(document.body);
        }
    }

    /* --------------------------------------------------------------------- *
     * Boot
     * --------------------------------------------------------------------- */

    async function init() {
        if (!headerRoot) {
            console.warn('[ps-header-footer] #ps-header-root not found; nothing rendered.');
            return;
        }

        state.data = await fetchData();
        state.data_loaded = true;

        headerRoot.innerHTML = renderHeader(state.data);
        if (footerRoot !== headerRoot) {
            footerRoot.innerHTML = renderFooter(state.data);
        } else {
            footerRoot.insertAdjacentHTML('beforeend', renderFooter(state.data));
        }

        bindEvents();
        bindFooterAccordions();
        bindFooterScrollEffects();
        sync();
    }

    init();
    // Independent of the header rendering: the bridge must still run on
    // templates where #ps-header-root is absent (checkout, for one).
    wishlistBridgeReady = syncWishlistBridge();
})();
